/**
 * Personal Chat Service
 *
 * Dedicated service for personal chat functionality using backend API.
 * Completely separate from team chat to prevent interference.
 */

interface StreamingOptions {
  onToken?: (token: string) => void;
  onComplete?: (response: string) => void;
  onError?: (error: Error) => void;
}

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

interface PersonalChatSession {
  sessionId: string;
  messages: ChatMessage[];
  createdAt: Date;
  lastMessageAt: Date;
}

class PersonalChatService {
  private apiUrl: string;
  private sessions: Map<string, PersonalChatSession>;

  constructor() {
    this.apiUrl = process.env.REACT_APP_API_URL || 'https://us-central1-teampulse-61474.cloudfunctions.net/api';
    this.sessions = new Map();
  }

  /**
   * Stream chat response from backend API
   * Uses OPENAI_API_KEY2 for GPT and ANTHROPIC_API_KEY2 for Claude
   */
  async streamChat(
    message: string,
    provider: 'openai' | 'anthropic' = 'openai',
    systemPrompt?: string,
    sessionId?: string,
    options?: StreamingOptions
  ): Promise<string> {
    try {
      // Determine model based on provider
      const model = provider === 'anthropic' ? 'claude-3-5-sonnet-20241022' : 'gpt-4o';

      // Get workspace ID from session storage
      const workspaceId = sessionStorage.getItem('currentWorkspaceId') || undefined;

      // Get conversation history from session (last 15 exchanges = 30 messages)
      const session = sessionId ? this.sessions.get(sessionId) : undefined;
      const conversationHistory = session ? session.messages.slice(-30) : [];

      // Call backend API
      const response = await fetch(`${this.apiUrl}/agents/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message,
          agentId: 'planner', // Default agent for personal chat
          systemPrompt: systemPrompt || 'You are a helpful AI assistant for AXTIV workspace. Provide clear, concise, and professional answers.',
          model,
          sessionId,
          workspaceId,
          conversationHistory // Send conversation history to backend
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        const errorMessage = errorData.error || `API request failed with status ${response.status}`;
        console.error('Personal chat API error:', errorMessage, errorData);
        throw new Error(errorMessage);
      }

      const data = await response.json();
      const fullResponse = data.response || '';

      // Save to session history
      if (sessionId) {
        this.updateSession(sessionId, message, fullResponse);
      }

      // Call completion callback
      options?.onComplete?.(fullResponse);

      return fullResponse;
    } catch (error) {
      console.error('Personal chat backend API call failed:', error);
      options?.onError?.(error as Error);
      throw error;
    }
  }

  /**
   * Get or create a session
   */
  getSession(sessionId: string): PersonalChatSession | undefined {
    return this.sessions.get(sessionId);
  }

  /**
   * Create a new session
   */
  createSession(sessionId: string): PersonalChatSession {
    const session: PersonalChatSession = {
      sessionId,
      messages: [],
      createdAt: new Date(),
      lastMessageAt: new Date()
    };
    this.sessions.set(sessionId, session);
    return session;
  }

  /**
   * Update session with new messages
   */
  private updateSession(sessionId: string, userMessage: string, assistantResponse: string): void {
    let session = this.sessions.get(sessionId);

    if (!session) {
      session = this.createSession(sessionId);
    }

    // Add user message
    session.messages.push({
      role: 'user',
      content: userMessage
    });

    // Add assistant response
    session.messages.push({
      role: 'assistant',
      content: assistantResponse
    });

    // Update last message timestamp
    session.lastMessageAt = new Date();

    // Keep only last 50 messages to prevent memory overflow
    if (session.messages.length > 50) {
      session.messages = session.messages.slice(-50);
    }

    this.sessions.set(sessionId, session);
  }

  /**
   * Clear session history
   */
  clearSession(sessionId: string): void {
    this.sessions.delete(sessionId);
  }

  /**
   * Clear all sessions
   */
  clearAllSessions(): void {
    this.sessions.clear();
  }

  /**
   * Get session message count
   */
  getSessionMessageCount(sessionId: string): number {
    const session = this.sessions.get(sessionId);
    return session ? session.messages.length : 0;
  }

  /**
   * Get all session IDs
   */
  getAllSessionIds(): string[] {
    return Array.from(this.sessions.keys());
  }
}

// Export singleton instance
export const personalChatService = new PersonalChatService();
export default personalChatService;
